import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../lib/api';
import type { ActionQueues } from '../lib/types';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader } from './ui/card';

function Queue({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <Card>
      <CardHeader title={`${title} · ${String(count)}`} />
      <CardContent className="grid gap-1 text-sm">{children}</CardContent>
    </Card>
  );
}

/** Главная: очереди «требует действия». Пусто — значит всё в порядке. */
export function ActionQueuesPanel() {
  const { t, i18n } = useTranslation();
  const queues = useQuery({
    queryKey: ['dashboard', 'queues'],
    queryFn: () => api<ActionQueues>('/dashboard/queues'),
    refetchInterval: 60_000,
  });
  const q = queues.data;
  if (!q) return null;
  const since = (d: string | null) => (d ? formatDateTime(d, i18n.language) : '');
  const orderLink = (o: { id: string; number: string; since: string | null }) => (
    <Link key={o.id} to="/orders/$orderId" params={{ orderId: o.id }} className="hover:underline">
      {o.number} <span className="text-muted-foreground">· {since(o.since)}</span>
    </Link>
  );
  const total = (Object.values(q) as unknown[][]).reduce((s: number, list) => s + list.length, 0);

  return (
    <section className="mb-6">
      <h2 className="mb-3 text-lg font-semibold">{t('queues.title')}</h2>
      {total === 0 ? <p className="text-sm text-muted-foreground">{t('queues.allClear')}</p> : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Queue title={t('queues.manualSourcing')} count={q.manualSourcing.length}>
          {q.manualSourcing.map(orderLink)}
        </Queue>
        <Queue title={t('queues.stuckPaid')} count={q.stuckPaid.length}>
          {q.stuckPaid.map(orderLink)}
        </Queue>
        <Queue title={t('queues.stuckWarehouse')} count={q.stuckWarehouse.length}>
          {q.stuckWarehouse.map(orderLink)}
        </Queue>
        <Queue title={t('queues.returns')} count={q.openReturns.length}>
          {q.openReturns.map((r) => (
            <Link key={r.id} to="/returns" className="hover:underline">
              {r.number} · {t(`ret.statuses.${r.status as 'requested'}`)}
            </Link>
          ))}
        </Queue>
        <Queue title={t('queues.refunds')} count={q.pendingRefunds.length}>
          {q.pendingRefunds.map((r) => (
            <Link key={r.id} to="/returns" className="hover:underline">
              {r.number} <span className="text-muted-foreground">· {since(r.since)}</span>
            </Link>
          ))}
        </Queue>
        <Queue title={t('queues.draftPos')} count={q.draftPurchaseOrders.length}>
          {q.draftPurchaseOrders.map((p) => (
            <Link key={p.id} to="/purchase-orders" className="hover:underline">
              {p.number}
            </Link>
          ))}
        </Queue>
        <Queue title={t('queues.lowStock')} count={q.lowStock.length}>
          {q.lowStock.map((s) => (
            <Link key={s.variantId} to="/warehouse/stock" className="hover:underline">
              {s.sku} · {s.name} · {s.available}
            </Link>
          ))}
        </Queue>
        <Queue title={t('queues.integrations')} count={q.integrations.length}>
          {q.integrations.map((i) => (
            <span key={i.id}>
              {i.name} <span className="text-muted-foreground">· {i.error}</span>
            </span>
          ))}
        </Queue>
      </div>
    </section>
  );
}
